import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { Database, Play, Table2, KeyRound, Search, AlertTriangle } from 'lucide-react';
import api from '../../services/api';
import { useI18n } from '../../i18n/I18nProvider';
import { errorText } from '../../lib/errors';
import { PageHeader, Card, CardHeader, DataTable, Button, Input, Textarea, ConfirmDialog, EmptyState, Spinner } from '../../ui';

const DISPLAY_LIMIT = 500;
const isReadQuery = (q) => /^\s*(select|pragma|with|explain)\b/i.test(q);

export default function SqlConsolePage() {
  const { t, fmt } = useI18n();
  const [tables, setTables] = useState(null);
  const [filter, setFilter] = useState('');
  const [active, setActive] = useState(null);
  const [schema, setSchema] = useState(null);
  const [query, setQuery] = useState('');
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null); // { rows, meta } | { error }
  const [confirmWrite, setConfirmWrite] = useState(false);

  useEffect(() => {
    api.get('/admin/database/tables')
      .then((res) => setTables(res.data.tables || []))
      .catch((err) => toast.error(errorText(err, t('common.error'))));
  }, [t]);

  const openTable = async (name) => {
    setActive(name);
    setSchema(null);
    const q = `SELECT * FROM "${name}" ORDER BY rowid DESC LIMIT 100`;
    setQuery(q);
    execute(q);
    try {
      const res = await api.get(`/admin/database/schema/${name}`);
      setSchema(res.data.columns || []);
    } catch (err) {
      toast.error(errorText(err, t('common.error')));
    }
  };

  const execute = async (sql) => {
    setRunning(true);
    try {
      const res = await api.post('/admin/database/query', { query: sql });
      setResult({ rows: res.data.result || [], meta: res.data.meta || {} });
    } catch (err) {
      setResult({ error: errorText(err, t('common.error')) });
    } finally {
      setRunning(false);
    }
  };

  const run = () => {
    const sql = query.trim();
    if (!sql || running) return;
    if (isReadQuery(sql)) execute(sql);
    else setConfirmWrite(true);
  };

  const onKeyDown = (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      run();
    }
  };

  const visibleTables = (tables || []).filter((name) => name.toLowerCase().includes(filter.trim().toLowerCase()));

  return (
    <>
      <PageHeader title={t('sql.title')} description={t('sql.subtitle')} />

      <div className="grid gap-6 lg:grid-cols-[260px_minmax(0,1fr)]">
        <Card className="self-start">
          <CardHeader title={t('sql.tables')} />
          <div className="px-3 pt-3">
            <Input icon={Search} value={filter} onChange={(e) => setFilter(e.target.value)} placeholder={t('common.search')} aria-label={t('common.search')} />
          </div>
          <nav className="max-h-[28rem] overflow-y-auto p-2 custom-scrollbar">
            {!tables && <div className="flex justify-center py-6"><Spinner /></div>}
            {visibleTables.map((name) => (
              <button
                key={name}
                type="button"
                onClick={() => openTable(name)}
                className={clsx(
                  'flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-left font-mono text-xs transition-colors',
                  active === name
                    ? 'bg-brand-50 text-brand-700 dark:bg-brand-950/50 dark:text-brand-300'
                    : 'text-zinc-700 hover:bg-zinc-100 dark:text-zinc-300 dark:hover:bg-zinc-800'
                )}
              >
                <Table2 className="h-3.5 w-3.5 shrink-0 opacity-60" />
                <span className="truncate">{name}</span>
              </button>
            ))}
          </nav>
          {active && (
            <div className="border-t border-zinc-200 p-3 dark:border-zinc-800">
              <p className="mb-2 px-1 text-xs font-medium uppercase tracking-wide text-zinc-500">{t('sql.columns')}</p>
              {!schema ? <div className="flex justify-center py-3"><Spinner /></div> : (
                <ul className="space-y-1">
                  {schema.map((c) => (
                    <li key={c.cid} className="flex items-center justify-between gap-2 px-1 font-mono text-[11px]">
                      <span className="flex min-w-0 items-center gap-1 text-zinc-800 dark:text-zinc-200">
                        {c.pk ? <KeyRound className="h-3 w-3 shrink-0 text-amber-500" aria-label="PK" /> : null}
                        <span className="truncate">{c.name}</span>
                      </span>
                      <span className="shrink-0 text-zinc-400">{c.type || 'ANY'}{c.notnull ? ' ·NN' : ''}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </Card>

        <div className="min-w-0 space-y-4">
          <Card>
            <CardHeader
              title={t('sql.query')}
              actions={<span className="hidden text-xs text-zinc-400 sm:inline">{t('sql.hint')}</span>}
            />
            <div className="p-4">
              <Textarea
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={onKeyDown}
                rows={6}
                spellCheck={false}
                placeholder="SELECT * FROM users LIMIT 50"
                className="font-mono text-[13px]"
                aria-label={t('sql.query')}
              />
              <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
                {!isReadQuery(query) && query.trim() ? (
                  <span className="inline-flex items-center gap-1.5 text-xs text-amber-700 dark:text-amber-400">
                    <AlertTriangle className="h-3.5 w-3.5" /> {t('sql.writeTitle')}
                  </span>
                ) : <span />}
                <Button icon={Play} onClick={run} loading={running} disabled={!query.trim()}>
                  {running ? t('sql.running') : t('sql.run')}
                </Button>
              </div>
            </div>
          </Card>

          <ResultView result={result} running={running} fmt={fmt} t={t} />
        </div>
      </div>

      <ConfirmDialog
        open={confirmWrite}
        onClose={() => setConfirmWrite(false)}
        onConfirm={() => { setConfirmWrite(false); execute(query.trim()); }}
        title={t('sql.writeTitle')}
        text={t('sql.writeText')}
        confirmLabel={t('sql.writeConfirm')}
      />
    </>
  );
}

function ResultView({ result, running, fmt, t }) {
  const rows = useMemo(
    () => (result?.rows || []).slice(0, DISPLAY_LIMIT).map((row, i) => ({ ...row, __rowKey: i })),
    [result]
  );
  const columns = useMemo(() => {
    const keys = rows.length ? Object.keys(rows[0]).filter((k) => k !== '__rowKey') : [];
    return keys.map((key) => ({
      key,
      header: <span className="font-mono normal-case">{key}</span>,
      sortable: true,
      render: (r) => <Cell value={r[key]} />,
    }));
  }, [rows]);

  if (!result && !running) {
    return <Card><EmptyState icon={Database} title={t('sql.pickTable')} /></Card>;
  }
  if (result?.error) {
    return (
      <div role="alert" className="rounded-xl border border-red-200 bg-red-50 p-4 font-mono text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300">
        {result.error}
      </div>
    );
  }
  if (result && !result.rows.length && result.meta.changes !== undefined) {
    return (
      <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-300">
        {t('sql.changes', { count: fmt.number(result.meta.changes) })}
      </div>
    );
  }

  const total = result?.rows.length ?? 0;
  return (
    <div>
      <p className="mb-2 text-xs text-zinc-500">
        {t('sql.rows', { count: fmt.number(total) })}
        {total > DISPLAY_LIMIT && <> · {t('sql.limited', { count: fmt.number(DISPLAY_LIMIT) })}</>}
      </p>
      <DataTable
        columns={columns}
        rows={rows}
        loading={running}
        rowKey={(r) => r.__rowKey}
        empty={<EmptyState icon={Database} title={t('sql.emptyResult')} />}
      />
    </div>
  );
}

function Cell({ value }) {
  if (value === null || value === undefined) return <span className="font-mono text-xs italic text-zinc-400">NULL</span>;
  const text = typeof value === 'object' ? JSON.stringify(value) : String(value);
  return <span className="block max-w-[18rem] truncate font-mono text-xs text-zinc-800 dark:text-zinc-200" title={text}>{text}</span>;
}
